/**
 * Additional attributes (user-defined attributes) — the platform's setup calls
 * (`/api/uda/{project}/<op>`, backend/app/routers/uda.py).
 *
 * The server decides who may set them up (builders, the organization's
 * func-admin, or the object's `uda_setup` right) and where they are stored
 * (the caller's tenant). Run-as headers (a project user in the Run view) are
 * attached by the shared axios client. The shared setup screen talks to these
 * through `UdaTransport` (`@twasta/page-runtime/uda`).
 */
import api from './client'
import type {
  UdaCoverage, UdaCoverageRequest, UdaExportResult, UdaGetResult, UdaHistoryEntry,
  UdaImportResult, UdaLayer, UdaMasterValues, UdaPatchResult, UdaResolution,
  UdaCheckResult, UdaCodeInfo, UdaSaveCodeResult,
} from '@twasta/page-runtime/uda'
import { udaBody, udaImportBody } from '@twasta/page-runtime/uda'

const enc = encodeURIComponent

async function post<T>(project: string, op: string, body: Record<string, unknown>): Promise<T> {
  const res = await api.post(`/uda/${enc(project)}/${op}`, body)
  return res.data as T
}

/** Whether the caller may set up attributes on this transaction. Never throws. */
export async function udaCanSetup(project: string, objName: string): Promise<boolean> {
  try {
    const r = await post<{ can_setup?: boolean }>(project, 'can-setup', udaBody(objName))
    return !!r?.can_setup
  } catch {
    return false
  }
}

export const udaGet = (project: string, objName: string) =>
  post<UdaGetResult>(project, 'get', udaBody(objName))

export const udaPatchDraft = (project: string, objName: string, body: UdaLayer,
                              baseVersion: number) =>
  post<UdaPatchResult>(project, 'patch-draft', udaBody(objName, { body, base_version: baseVersion }))

export const udaMasterValues = (project: string, objName: string, formNo: string, via: string,
                                column: string, opts: { q?: string; limit?: number } = {}) =>
  post<UdaMasterValues>(project, 'master-values',
    udaBody(objName, { form_no: formNo, via, column, q: opts.q, limit: opts.limit }))

export const udaCoverage = (project: string, objName: string, req: UdaCoverageRequest) =>
  post<UdaCoverage>(project, 'coverage', udaBody(objName, { ...req }))

export const udaPreview = (project: string, objName: string, formNo: string,
                           drivers: Record<string, string | null>, body?: UdaLayer) =>
  post<UdaResolution>(project, 'preview', udaBody(objName, { form_no: formNo, drivers, body }))

export const udaCheckCode = (project: string, objName: string, fnName: string, source: string) =>
  post<UdaCheckResult>(project, 'check-code', udaBody(objName, { fn_name: fnName, source }))

export const udaGetCode = (project: string, objName: string, fnName: string) =>
  post<UdaCodeInfo>(project, 'get-code', udaBody(objName, { fn_name: fnName }))

export const udaSaveCode = (project: string, objName: string, fnName: string, source: string) =>
  post<UdaSaveCodeResult>(project, 'save-code', udaBody(objName, { fn_name: fnName, source }))

export const udaPublish = (project: string, objName: string, baseVersion: number,
                           summary?: string) =>
  post<{ version: number; problems?: string[] }>(project, 'publish',
    udaBody(objName, { base_version: baseVersion, summary }))

export const udaHistory = (project: string, objName: string) =>
  post<{ versions: UdaHistoryEntry[] }>(project, 'history', udaBody(objName))

export const udaRollback = (project: string, objName: string, version: number) =>
  post<{ version: number; draft_version: number }>(project, 'rollback',
    udaBody(objName, { version }))

export const udaImport = (project: string, objName: string, formNo: string,
                          input: { rows?: string[][]; fileBase64?: string; filename?: string;
                            mode?: 'replace' | 'append'; baseVersion?: number }) =>
  post<UdaImportResult>(project, 'import', udaImportBody(objName, formNo, input))

export const udaExport = (project: string, objName: string, formNo: string) =>
  post<UdaExportResult>(project, 'export', udaBody(objName, { form_no: formNo }))

export const udaCatalogTables = (project: string, objName: string) =>
  post<{ tables: Array<{ name: string }> }>(project, 'catalog/tables', udaBody(objName))

export const udaCatalogColumns = (project: string, objName: string, table: string) =>
  post<{ columns: Array<{ column_name: string; data_type?: string }> }>(
    project, 'catalog/columns', udaBody(objName, { table }))
